import type {
  AxiosAdapter,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

type PendingResponse = Promise<AxiosResponse>;

const headerValue = (config: InternalAxiosRequestConfig, name: string): string => {
  const value = config.headers?.get?.(name);

  return typeof value === 'string' ? value : '';
};

const stableParams = (value: unknown): string => {
  if (value instanceof URLSearchParams) return value.toString();
  if (!value || typeof value !== 'object') return String(value ?? '');
  if (Array.isArray(value)) return `[${value.map(stableParams).join(',')}]`;

  return `{${Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, item]) => `${key}:${stableParams(item)}`)
    .join(',')}}`;
};

const serializedParams = (config: InternalAxiosRequestConfig): string => {
  const serializer = config.paramsSerializer as {
    serialize?: (params: unknown) => string;
  } | undefined;

  return serializer?.serialize
    ? serializer.serialize(config.params)
    : stableParams(config.params);
};

const requestKey = (config: InternalAxiosRequestConfig): string => [
  config.baseURL || '',
  config.url || '',
  serializedParams(config),
  headerValue(config, 'Authorization'),
  headerValue(config, 'X-Client-Country'),
  config.responseType || 'json',
  config.withCredentials ? 'credentials' : 'no-credentials',
].join('|');

const canCoalesce = (config: InternalAxiosRequestConfig): boolean => (
  (config.method || 'get').toLowerCase() === 'get'
  && !config.signal
  && !config.cancelToken
  && !config.onDownloadProgress
  && config.responseType !== 'stream'
);

/**
 * Share identical GETs that overlap in time. React development StrictMode and
 * independently mounted planning widgets can otherwise issue the same network
 * read concurrently. Only in-flight requests are shared: completed responses
 * are never cached, so explicit refreshes and event-driven invalidation remain
 * fresh.
 */
export const createCoalescingGetAdapter = (delegate: AxiosAdapter): AxiosAdapter => {
  const pending = new Map<string, PendingResponse>();

  // eslint-disable-next-line max-statements
  return async (config) => {
    if (!canCoalesce(config)) return delegate(config);

    const key = requestKey(config);
    let request = pending.get(key);

    if (!request) {
      request = Promise.resolve(delegate(config));
      pending.set(key, request);
      request.finally(() => {
        if (pending.get(key) === request) pending.delete(key);
      }).catch(() => { /* the caller receives the original rejection */ });
    }

    const response = await request;

    // Axios responses expose their originating config. Preserve that contract
    // for every subscriber rather than leaking the first caller's config.
    return response.config === config ? response : { ...response, config };
  };
};
